/**
 * API error catalog.
 *
 * Every failure the API can return is enumerated here together with its HTTP
 * status. Both the Worker and the client import this module, which keeps the
 * client's error handling exhaustive instead of string-matching messages.
 */

export const API_ERROR_CODES = [
  'invalid_request',
  'invalid_email',
  'rate_limited',
  'daily_limit_reached',
  'invalid_code',
  'code_expired',
  'too_many_attempts',
  'unauthorized',
  'forbidden',
  'invite_required',
  'invite_invalid',
  'invite_used',
  'invite_expired',
  'invite_email_mismatch',
  'invite_apply_pending',
  'invite_apply_cooldown',
  'invite_apply_closed',
  'account_banned',
  'registration_closed',
  'not_found',
  'method_not_allowed',
  'event_archived',
  'not_joined',
  'too_many_departures',
  'no_candidates',
  'round_budget_exhausted',
  'upstream_unavailable',
  'internal_error',
] as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[number]

const STATUS_BY_CODE: Record<ApiErrorCode, number> = {
  invalid_request: 400,
  invalid_email: 400,
  rate_limited: 429,
  daily_limit_reached: 429,
  invalid_code: 400,
  code_expired: 400,
  too_many_attempts: 429,
  unauthorized: 401,
  forbidden: 403,
  invite_required: 403,
  invite_invalid: 400,
  invite_used: 409,
  invite_expired: 410,
  invite_email_mismatch: 403,
  invite_apply_pending: 409,
  invite_apply_cooldown: 429,
  invite_apply_closed: 403,
  account_banned: 403,
  registration_closed: 403,
  not_found: 404,
  method_not_allowed: 405,
  event_archived: 409,
  not_joined: 403,
  too_many_departures: 409,
  no_candidates: 422,
  round_budget_exhausted: 429,
  upstream_unavailable: 502,
  internal_error: 500,
}

export function statusForErrorCode(code: ApiErrorCode): number {
  return STATUS_BY_CODE[code]
}

export function isApiErrorCode(value: string): value is ApiErrorCode {
  return (API_ERROR_CODES as readonly string[]).includes(value)
}

/** Translation key of the human readable message for an error code. */
export function errorMessageKey(code: ApiErrorCode): string {
  return `error.${code}`
}
